import { FULL_SCREEN_EVENTS, isFullScreenEvent } from "./notificationRules";

// Channels are immutable. Old ids passed a sound name the plugin maps to res/raw/<name>, which no tenant APK
// bundles, so they were silent; these are created without a sound so Android uses the phone's notification tone.
export const ORDERS_CHANNEL_ID = "bizil_orders_high_v3";
export const NEW_ORDER_CHANNEL_ID = "bizil_new_order_alert_v2";
// Where a push lands when the tenant turned sound off for that event. A channel's sound is fixed when it is
// created and a payload can't override it, so "silent" has to be a channel of its own.
export const SILENT_CHANNEL_ID = "bizil_orders_silent_v1";

// Sent with the FCM token to mark a bundle that manages its own channels. The backend needs to tell "old app,
// whose legacy channel still exists" from "current app, which deleted those ids and failed to create the new
// ones" — addressing a channel the device doesn't have makes Android drop the notification silently.
export const CHANNEL_SUPPORT = "v3";

/**
 * The channel that actually plays the tenant's uploaded new-order sound, created ONLY when this build is known
 * to have compiled that exact sound version in. Version-stamped because channels are immutable: a tenant that
 * uploads a new sound needs a new id, and an old id must never be repurposed.
 *
 * The interlock matters more than it looks. Pointing a channel at a ``res/raw`` file that isn't there makes it
 * **permanently silent** — not "falls back to default" — and it cannot be repaired without another new id
 * (landmine 16; this is how every Android order push went soundless once already). So we create it only when
 * the backend confirms a build of THIS app bundled THIS version.
 */
export const soundChannelId = (version) => `bizil_new_order_alert_v3_s${version}`;
/** The ``res/raw`` resource name the build writes for a given sound version. Must match download_notification_sound.py. */
export const soundResourceName = (soundKey, version) => `${soundKey}_v${version}`;

/** Marker the build stamps into the bundle's own capacitor.config.json — see download_notification_sound.py. */
export const SOUND_MARKER_PREFIX = "BizilSnd/";

/**
 * The sound resource THIS installed binary actually contains, read from the WebView User-Agent.
 *
 * The APK is a WebView shell, so a phone still on last month's build downloads exactly the same JavaScript as
 * a phone updated this morning. Nothing served from the cloud can therefore distinguish them — which is why
 * the answer has to come from inside the bundle. ``appendUserAgent`` is set at build time by the same step
 * that writes ``res/raw/<name>``, so a marker present here means that file is present too.
 *
 * Returns null on the web, in an old APK with no marker, and on anything we cannot parse — all of which mean
 * "do not create a sound-bearing channel".
 */
export function deviceBundledSoundName(userAgent) {
    const m = new RegExp(`${SOUND_MARKER_PREFIX}([A-Za-z0-9_.-]+)`).exec(userAgent || "");
    return m ? m[1] : null;
}

/**
 * Whether this app build can safely be told to play the tenant's custom sound.
 *
 * ``deviceSoundName`` is what the binary reports it bundled (``deviceBundledSoundName``). It must equal the
 * resource the tenant's CURRENT sound version needs — an APK built for v2 must not be told to play v3.
 *
 * Any doubt (no sound configured, no marker, a marker for a different sound or version) answers null and the
 * tenant keeps the phone's default tone. That is the only safe direction: pointing an Android channel at a
 * ``res/raw`` file that is not there makes it **permanently silent** rather than falling back, and channels
 * are immutable, so the mistake cannot be repaired without burning another id (landmine 16).
 *
 * Deliberately NOT keyed off ``tenant.new_order_alert_config.bundled_sound_versions``: that records what the
 * most recent BUILD compiled, which says nothing about the build a given phone is running. Reading it here is
 * what would have made an old APK create a silent channel after a cloud deploy. It stays on the tenant record
 * for Super-Admin visibility only.
 */
export function bundledSoundFor(tenantNewOrderCfg, deviceSoundName) {
    const key = tenantNewOrderCfg?.sound_key;
    const version = tenantNewOrderCfg?.version;
    if (!key || typeof version !== "number") return null;
    const want = soundResourceName(key, version);
    if (!deviceSoundName || deviceSoundName !== want) return null;
    return { id: soundChannelId(version), sound: want, version };
}

const isLegacyOrders = (id) => id === "bizil_orders_high" || id.startsWith("bizil_orders_high_v2");
const isLegacyNewOrder = (id) => id.startsWith("bizil_new_order_alert_v1");

// IMPORTANCE_NONE means the user switched the old channel off; the replacement starts off too rather than re-enabling it.
function importanceFor(existingChannels, isLegacy) {
    const old = (existingChannels || []).filter((c) => isLegacy(c.id));
    return old.length > 0 && old.every((c) => c.importance === 0) ? 0 : 5;
}

/**
 * Whether this tenant wants new orders on their own distinct channel, rather than sharing the routine one with
 * "out for delivery". True for the legacy Super-Admin alert, AND — since iter159b — for a tenant that set any
 * alert event to FULL_SCREEN on the Notifications page. Mirrors the same condition in backend fcm.py: if the two
 * disagree the backend addresses a channel this device never created, and Android drops the notification.
 */
export function wantsDistinctNewOrderChannel(tenantNewOrderCfg, notificationRules = null) {
    if (tenantNewOrderCfg?.sound_key || tenantNewOrderCfg?.loud_mode_enabled) return true;
    const tenant = { new_order_alert_config: tenantNewOrderCfg, notification_rules: notificationRules };
    return [...FULL_SCREEN_EVENTS].some((e) => isFullScreenEvent(tenant, e));
}

export function androidChannelSpecs(tenantNewOrderCfg, existingChannels = [], notificationRules = null, deviceSoundName = null) {
    const specs = [{
        id: ORDERS_CHANNEL_ID,
        name: "Orders & Deliveries",
        description: "Order status updates and delivery progress",
        importance: importanceFor(existingChannels, isLegacyOrders),
        visibility: 1,
        vibration: true,
        lights: true,
        lightColor: "#10B981",
    }];
    // Only for tenants that opted into a distinct new-order alert — mirrors the gate in backend fcm.py.
    if (wantsDistinctNewOrderChannel(tenantNewOrderCfg, notificationRules)) {
        specs.push({
            id: NEW_ORDER_CHANNEL_ID,
            name: "New Orders",
            description: "A new order needs your attention — kept louder and distinct on purpose",
            importance: importanceFor(existingChannels, isLegacyNewOrder),
            visibility: 1,
            vibration: true,
            lights: true,
            lightColor: "#F59E0B",
        });
    }
    // The custom-sound channel, when — and only when — this build is confirmed to carry that sound version.
    const custom = wantsDistinctNewOrderChannel(tenantNewOrderCfg, notificationRules)
        ? bundledSoundFor(tenantNewOrderCfg, deviceSoundName)
        : null;
    if (custom) {
        specs.push({
            id: custom.id,
            name: "New Orders",
            description: "A new order needs your attention — plays your business's alert sound",
            importance: importanceFor(existingChannels, isLegacyNewOrder),
            visibility: 1,
            vibration: true,
            lights: true,
            lightColor: "#F59E0B",
            // Maps to res/raw/<name> — safe here precisely because the backend confirmed this build has it.
            sound: custom.sound,
        });
    }
    // Always registered so an admin can turn sound off for an event without the notification itself disappearing.
    // IMPORTANCE_DEFAULT: it still reaches the shade and the lock screen, just without a tone or a heads-up banner.
    specs.push({
        id: SILENT_CHANNEL_ID,
        name: "Quiet updates",
        description: "Notifications your business chose to deliver without a sound",
        importance: 3,
        visibility: 1,
        vibration: false,
        lights: true,
        lightColor: "#10B981",
    });
    return specs;
}

/** Old silent order channels still registered on the device, safe to delete. */
export function legacyOrderChannelIds(existingIds) {
    return (existingIds || []).filter((id) => isLegacyOrders(id) || isLegacyNewOrder(id));
}
